import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Bone, Activity, Heart, Wind, Award, Lock, ChevronDown, ArrowRight, X, Volume2, BookOpen, Target, Search, Info } from "lucide-react";
import KennelSEO, { localBusinessSchema, breadcrumbSchema, faqSchema } from "./KennelSEO";

const BASE = "https://wolfevomkatzenberg.pl";

/* ─── LAYERS ─── */
const LAYERS = [
  { id: "external", name: "Budowa zewnętrzna", icon: Eye, available: true, image: "/german_shepherd_anatomy.png" },
  { id: "skeletal", name: "Układ kostny", icon: Bone, available: false, image: "/anatomy_skeletal.png", desc: "Szkielet owczarka — czaszka, kręgosłup, miednica, kości kończyn" },
  { id: "muscular", name: "Układ mięśniowy", icon: Activity, available: false, image: "/anatomy_muscular.png", desc: "Mięśnie odpowiedzialne za ruch, siłę i wytrzymałość" },
  { id: "organs", name: "Narządy wewnętrzne", icon: Heart, available: false, image: "/anatomy_organs.png", desc: "Układ pokarmowy, oddechowy, krążenia i nerwowy" },
  { id: "movement", name: "Ruch i biomechanika", icon: Wind, available: false, image: "/anatomy_movement.png", desc: "Kłus, krok, przenoszenie napędu, znaczenie górnej linii" },
  { id: "fci", name: "Standard FCI", icon: Award, available: false, image: "/german_shepherd_anatomy.png", desc: "Cechy pożądane, proporcje i kryteria oceny sędziowskiej" },
];

/* ─── INFO TABS ─── */
const INFO_TABS = [
  { id: "opis", label: "Opis", icon: BookOpen, level: "Dla każdego" },
  { id: "funkcja", label: "Funkcja", icon: Activity, level: "Dla właściciela" },
  { id: "znaczenie", label: "Dla rasy", icon: Target, level: "Dla hodowcy" },
  { id: "ocena", label: "Ocena", icon: Search, level: "Sędzia / wystawa" },
];

/* ─── BODY PARTS DATA ─── */
const externalParts = [
  {
    id: "glowa", name: "Głowa", x: 20, y: 26,
    opis: "Głowa owczarka niemieckiego powinna być proporcjonalna do ciała, klinowata, sucha i umiarkowanie szeroka między uszami. Czoło lekko sklepione, bruzda czołowa słabo zaznaczona. Stop wyraźny, ale nie za ostry. Kufa mocna, prosta, o równoległych liniach z czaszką.",
    funkcja: "Głowa jest centrum zmysłów psa — wzrok, słuch, węch i dotyk. Kształt klinowaty zapewnia optymalny stosunek siły szczęk do długości kufy, co jest ważne dla psa roboczego. Prawidłowa budowa czaszki umożliwia właściwe osadzenie mózgu i narządów zmysłów.",
    znaczenie: "W typie rasowym owczarka niemieckiego głowa jest jednym z kluczowych elementów rozpoznawczych. Proporcja czaszka:kufa powinna wynosić 50:50. Zbyt ciężka, zbyt lekka lub nieproporcjonalna głowa obniża ocenę na wystawie i może wskazywać na problemy z typem.",
    ocena: "Sędzia ocenia: proporcje czaszki do kufy, kształt klinowaty, wyrazistość stopu, równoległość linii, kolor nosa (zawsze czarny), pełnozębowość (42 zęby) i prawidłowy zgryz nożycowy. Oczy ciemne, migdałowate, lekko skośne."
  },
  {
    id: "uszy", name: "Uszy", x: 26, y: 11,
    opis: "Uszy stojące, średniej wielkości, szeroko osadzone u podstawy, noszone pionowo i symetrycznie. Końce lekko zaostrzone, skierowane do przodu. Małżowina uszna otwarta od przodu.",
    funkcja: "Uszy pełnią kluczową rolę w komunikacji psa i odbiorze dźwięków. Stojące uszy pozwalają na precyzyjną lokalizację źródła dźwięku, co jest niezbędne u psa roboczego i stróżującego.",
    znaczenie: "Stojące uszy to cecha charakterystyczna owczarka niemieckiego. Uszy obcięte, wiszące lub niesymetryczne są poważną wadą. U szczeniąt uszy mogą stawać do 6–8 miesiąca życia — opóźniony czas stawiania może wynikać z genetyki lub problemów żywieniowych.",
    ocena: "Sędzia ocenia: wielkość, osadzenie, symetrię, kierunek i sposób noszenia uszu. Uszy zbyt blisko osadzone, wiszące, załamane, zbyt duże lub miękkie u nasady to wady obniżające ocenę."
  },
  {
    id: "szyja", name: "Szyja", x: 27, y: 24,
    opis: "Szyja silna, muskularna, bez podgardla, tworząca z tułowiem kąt około 45 stopni. Powinna stanowić około 35% wysokości w kłębie. Przy ożywieniu unoszona wysoko, przy kłusie obniżana.",
    funkcja: "Szyja jest pomostem między głową a tułowiem, odpowiada za przenoszenie sił i równowagę psa w ruchu. Silna muskulatura szyi jest niezbędna przy pracy obronnej i trzymaniu przedmiotów.",
    znaczenie: "Prawidłowa szyja nadaje owczarkowi niemieckiemu szlachetny wygląd. Zbyt krótka lub zbyt długa szyja zaburza proporcje i wpływa negatywnie na ruch. Podgardle (luźna skóra) jest wadą.",
    ocena: "Sędzia ocenia: długość, kąt osadzenia, muskulaturę, brak podgardla, płynne przejście w kłąb. Szyja powinna tworzyć harmonijną linię z resztą sylwetki."
  },
  {
    id: "klab", name: "Kłąb", x: 33, y: 17,
    opis: "Kłąb to najwyższy punkt linii grzbietu — powinien być długi, wyraźnie zaznaczony, wysoki i dobrze umięśniony. Stanowi punkt odniesienia do mierzenia wysokości psa.",
    funkcja: "Kłąb jest miejscem przyczepu mięśni łopatki i kręgosłupa. Wysoki, dobrze rozwinięty kłąb zapewnia lepszy kąt łopatki, co bezpośrednio przekłada się na wydajność ruchu i długość kroku przedniego.",
    znaczenie: "U owczarka niemieckiego kłąb jest jednym z najważniejszych elementów budowy. Wysoki kłąb nadaje charakterystyczną linię górną rasy. Wysokość w kłębie: psy 60–65 cm, suki 55–60 cm. Kłąb nisko osadzony lub słabo zaznaczony to poważna wada.",
    ocena: "Sędzia ocenia: wyrazistość kłębu, jego długość i wysokość w stosunku do grzbietu. Kłąb powinien wyraźnie dominować nad linią grzbietu, tworząc płynne przejście."
  },
  {
    id: "grzbiet", name: "Grzbiet", x: 43, y: 30,
    opis: "Grzbiet prosty, silny, dobrze umięśniony. Linia górna biegnie bez przerwy od kłębu przez długi grzbiet do lekko opadającego zadu. Nie powinien być ani zbyt długi, ani zbyt krótki.",
    funkcja: "Grzbiet przenosi napęd z kończyn tylnych na przednie. Silny grzbiet utrzymuje stabilność ciała w ruchu i pracy. Elastyczność grzbietu pozwala na wydajny kłus dalekiego zasięgu.",
    znaczenie: "Proporcja długości tułowia do wysokości w kłębie powinna wynosić 110–117%. Grzbiet karpiowaty (wypukły) lub lordotyczny (wklęsły) to wady. U owczarka niemieckiego grzbiet powinien lekko opadać ku zadowi, ale nie może być przesadnie pochyły.",
    ocena: "Sędzia ocenia: linię górną (płynność, brak załamań), umięśnienie, proporcje długości do wysokości. Grzbiet musi być mocny i sprężysty, nie miękki ani kołyszący się w ruchu."
  },
  {
    id: "ledz", name: "Lędźwie", x: 52, y: 34,
    opis: "Lędźwie szerokie, silne, dobrze umięśnione. Łączą grzbiet z zadem i są jednym z najważniejszych elementów przenoszenia napędu w ruchu. Powinny być krótkie i mocne.",
    funkcja: "Lędźwie to 'most napędowy' - przenoszą siłę z kończyn tylnych przez miednicę na grzbiet. Silne lędźwie zapewniają sprężysty, wydajny ruch i stabilność na zakrętach.",
    znaczenie: "Zbyt długie lub słabe lędźwie powodują 'kołysanie' zadu w ruchu i zmniejszają efektywność kłusu. U owczarka niemieckiego krótkie, mocne lędźwie to cecha pożądana, szczególnie ważna u psów roboczych.",
    ocena: "Sędzia ocenia: szerokość, umięśnienie, długość (powinny być relatywnie krótkie). Słabe lędźwie objawiają się miękkością linii górnej w ruchu."
  },
  {
    id: "zad", name: "Zad", x: 62, y: 39,
    opis: "Zad długi, lekko opadający (kąt około 23 stopni), przechodzący płynnie w nasadę ogona. Dobrze umięśniony i szeroki.",
    funkcja: "Zad jest punktem przyczepu najsilniejszych mięśni tylnych kończyn. Prawidłowy kąt nachylenia zadu pozwala na efektywne przenoszenie napędu i optymalny wykrok tylnych kończyn.",
    znaczenie: "Kąt nachylenia zadu to jeden z najbardziej dyskutowanych elementów u owczarka. Zad stromy ogranicza zakres ruchu kończyn tylnych. Zad płaski zaburza linię górną. Optymalnie: 23° w stosunku do poziomu.",
    ocena: "Sędzia ocenia: długość zadu, kąt nachylenia, umięśnienie, szerokość. Zad zbyt stromy lub zbyt płaski to wada wpływająca na ruch i sylwetkę."
  },
  {
    id: "ogon", name: "Ogon", x: 76, y: 65,
    opis: "Ogon sięga co najmniej do stawu skokowego. Pokryty gęstą sierścią, od spodu nieco dłuższą (tzw. szczotka). W spoczynku noszony w łagodnym łuku w dół.",
    funkcja: "Ogon pełni rolę steru i przeciwwagi w ruchu — stabilizuje ciało podczas szybkiego biegu, zwrotów i skoków. Jest też ważnym narzędziem komunikacji — wyraża emocje psa.",
    znaczenie: "Ogon nie powinien być noszony powyżej linii grzbietu, nawet przy ożywieniu. Ogon skręcony, zbyt krótki, zawinięty na bok lub noszony stale wysoko to wady. Długość ogona odpowiada prawidłowym proporcjom kręgosłupa.",
    ocena: "Sędzia ocenia: długość, sposób noszenia, kształt (łagodny łuk), brak załamań. Ogon powinien harmonijnie przedłużać linię grzbietu."
  },
  {
    id: "klatka", name: "Klatka piersiowa", x: 32, y: 48,
    opis: "Klatka piersiowa umiarkowanie szeroka, z długim i wyraźnym mostkiem. Żebra umiarkowanie sklepione — ani beczkowate, ani płaskie. Głębokość klatki stanowi około 45–48% wysokości w kłębie.",
    funkcja: "Klatka piersiowa chroni serce i płuca, a jej pojemność bezpośrednio wpływa na wydolność psa. Prawidłowa budowa klatki zapewnia odpowiednią wentylację płuc podczas intensywnego wysiłku.",
    znaczenie: "U owczarka niemieckiego wymagana jest głęboka, ale nie przesadnie szeroka klatka piersiowa. Zbyt beczkowata klatka utrudnia swobodny ruch kończyn przednich. Zbyt płaska — zmniejsza pojemność oddechową.",
    ocena: "Sędzia ocenia: głębokość (do łokcia), szerokość, kształt żeber, długość mostka. Prawidłowa klatka piersiowa wpływa na postawę kończyn przednich i ogólną harmonię sylwetki."
  },
  {
    id: "przednie", name: "Kończyny przednie", x: 30, y: 65,
    opis: "Łopatki długie, skośnie ułożone (kąt 45°), dobrze umięśnione. Ramię tworzy z łopatką kąt około 90°. Przedramiona proste i równoległe widziane z przodu. Śródręcze lekko skośne (ok. 20–22°).",
    funkcja: "Kończyny przednie amortyzują uderzenia podczas ruchu i odpowiadają za kierunek biegu. Prawidłowe kątowanie łopatki zapewnia długi krok przedni i efektywne amortyzowanie skoków.",
    znaczenie: "Kątowanie kończyn przednich to jeden z najczęściej ocenianych elementów. Zbyt strome ustawienie łopatki skraca krok i zwiększa obciążenie stawów. U owczarka niemieckiego prawidłowe kątowanie jest kluczowe dla wydajnego kłusu.",
    ocena: "Sędzia ocenia: kąt łopatki do poziomu (45°), kąt łopatka–ramię (ok. 90°), równoległość przedramion, kąt śródręcza, ścisłość łokci. Łokcie nie powinny być odstawione ani podciągnięte."
  },
  {
    id: "tylne", name: "Kończyny tylne", x: 64, y: 58,
    opis: "Udo szerokie i dobrze umięśnione. Staw kolanowy dobrze kątowany. Podudzie i udo zbliżonej długości. Staw skokowy mocny, sprężysty, dobrze kątowany. Widziane z tyłu — kończyny proste i równoległe.",
    funkcja: "Kończyny tylne to główny napęd psa — generują siłę, która przenosi się przez lędźwie na resztę ciała. Prawidłowe kątowanie zapewnia wydajny, dalekosięgny kłus bez nadmiernego wysiłku.",
    znaczenie: "U owczarka niemieckiego kątowanie kończyn tylnych jest kluczowym elementem typu rasowego. Zbyt strome kątowanie ogranicza zasięg kroku. Zbyt głębokie kątowanie (tzw. 'przekątowanie') obciąża stawy i więzadła.",
    ocena: "Sędzia ocenia: kąt stawu kolanowego, kąt stawu skokowego, równoległość kończyn z tyłu, umięśnienie uda, sprężystość stawu skokowego. Wilcze pazury są niedopuszczalne."
  },
  {
    id: "lapy", name: "Łapy", x: 30, y: 86,
    opis: "Łapy okrągłe, zwarte, z dobrze wysklepionymi palcami (tzw. łapa kocia). Opuszki twarde, ciemne. Pazury krótkie, mocne, ciemne. Tylne łapy nieco mniejsze od przednich.",
    funkcja: "Łapy to punkt kontaktu psa z podłożem — absorbują wstrząsy, zapewniają przyczepność i umożliwiają precyzyjne ruchy. Twarde opuszki chronią przed urazami na różnym terenie.",
    znaczenie: "Łapa płaska, rozpłaszczona lub miękka to poważna wada u psa roboczego. Owczarek niemiecki powinien mieć zwarte, sprężyste łapy odporne na ścieranie, co jest szczególnie istotne przy długotrwałej pracy w terenie.",
    ocena: "Sędzia ocenia: zwartość łapy, wysklepienie palców, stan opuszek, długość i kolor pazurów, ogólną kondycję. Łapa 'zajęcza' (wydłużona) lub rozpłaszczona obniża ocenę."
  },
  {
    id: "siersc", name: "Szata / Sierść", x: 42, y: 42,
    opis: "Sierść podwójna — gęsty podszerstek i twardy, przylegający włos okrywowy. Na szyi, udach i ogonie sierść dłuższa. Na głowie, uszach i łapach — krótka i przylegająca.",
    funkcja: "Podwójna szata chroni psa przed warunkami atmosferycznymi — zimnem, deszczem, upałem i urazami mechanicznymi. Podszerstek izoluje termicznie, a włos okrywowy odpycha wodę i brud.",
    znaczenie: "Dopuszczalne umaszczenia: czarne z brązowym/żółtym/czerwonym podpaleniem, jednolicie czarne, szaro-podpalane. Mały biały znaczek na piersi dopuszczalny. Sierść zbyt długa, falista lub brak podszerstka to wady.",
    ocena: "Sędzia ocenia: jakość włosa (twardość), gęstość podszerstka, długość i przyleganie, umaszczenie i intensywność podpalania, czystość koloru. Szata powinna podkreślać muskulaturę i budowę ciała."
  }
];

/* ─── FAQ DATA ─── */
const anatomyFaq = [
  {
    q: "Jak wygląda prawidłowa budowa owczarka niemieckiego?",
    a: "Prawidłowy owczarek niemiecki to pies średniej wielkości, silny, dobrze umięśniony, o lekko wydłużonej sylwetce (proporcja 110–117%). Powinien mieć wysoki kłąb, prosty grzbiet lekko opadający ku zadowi, głęboką klatkę piersiową i dobrze kątowane kończyny. Głowa klinowata, uszy stojące, ogon sięgający stawu skokowego."
  },
  {
    q: "Co oznacza kątowanie kończyn u owczarka niemieckiego?",
    a: "Kątowanie to kąty tworzone przez kości w stawach. U owczarka niemieckiego prawidłowe kątowanie kończyn przednich (łopatka 45°, kąt łopatka–ramię ok. 90°) i tylnych (dobrze kątowany staw kolanowy i skokowy) zapewnia wydajny, dalekosięgny kłus. Zbyt strome lub zbyt głębokie kątowanie to wady wpływające na ruch i zdrowie stawów."
  },
  {
    q: "Na co patrzy sędzia na wystawie owczarków niemieckich?",
    a: "Sędzia ocenia: typ rasowy (czy pies wygląda jak owczarek niemiecki), proporcje ciała, budowę głowy, osadzenie i noszenie uszu, linię górną (kłąb–grzbiet–zad), kątowanie kończyn, głębokość klatki piersiowej, jakość ruchu (kłus), temperament i charakter, szatę i umaszczenie. Ważna jest harmonia całości."
  },
  {
    q: "Dlaczego kłąb jest tak ważny u owczarka niemieckiego?",
    a: "Kłąb to najwyższy punkt linii grzbietu i punkt odniesienia do mierzenia wysokości psa (60–65 cm u psów, 55–60 cm u suk). Wysoki, dobrze rozwinięty kłąb zapewnia lepsze kątowanie łopatki, co bezpośrednio przekłada się na długość kroku przedniego i wydajność ruchu. Nadaje też charakterystyczną sylwetkę rasy."
  },
  {
    q: "Jak budowa ciała wpływa na ruch owczarka niemieckiego?",
    a: "Owczarek niemiecki jest rasą kłusaka dalekiego zasięgu. Prawidłowa budowa — wysoki kłąb, dobrze kątowane kończyny, mocne lędźwie i odpowiedni kąt zadu — pozwala na wydajny, oszczędzający energię kłus. Pies z prawidłową budową może poruszać się godzinami bez zmęczenia."
  },
  {
    q: "Co to jest prawidłowa górna linia u owczarka?",
    a: "Górna linia to profil grzbietu widziany z boku — biegnie od kłębu przez grzbiet do zadu. U owczarka niemieckiego powinna być płynna, bez załamań, lekko opadająca od wysokiego kłębu ku zadowi. Linia nie powinna być przerywana, łukowata ani płaska."
  },
  {
    q: "Jakie proporcje ciała powinien mieć owczarek niemiecki?",
    a: "Stosunek długości tułowia do wysokości w kłębie: 110–117%. Głębokość klatki piersiowej: 45–48% wysokości w kłębie. Proporcja czaszki do kufy: 50:50. Szyja: ok. 35% wysokości w kłębie. Te proporcje zapewniają harmonijną, funkcjonalną sylwetkę."
  },
  {
    q: "Dlaczego budowa psa ma znaczenie przy wyborze szczenięcia?",
    a: "Budowa bezpośrednio wpływa na zdrowie, ruch i jakość życia psa. Prawidłowa budowa zmniejsza ryzyko problemów ortopedycznych (dysplazja, artretyzm), zapewnia wydajny ruch i wytrzymałość. Wybierając szczenię z hodowli dbającej o budowę rodziców, zwiększasz szansę na zdrowego, funkcjonalnego psa."
  }
];

/* ─── SCHEMAS ─── */
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Anatomia Owczarka Niemieckiego – interaktywny przewodnik po budowie rasy",
  "description": "Szczegółowy, interaktywny atlas anatomii owczarka niemieckiego. Budowa zewnętrzna, układ kostny, mięśnie i standard FCI.",
  "author": { "@type": "Organization", "name": "Wolfe vom Katzenberg" },
  "publisher": { "@type": "Organization", "name": "Wolfe vom Katzenberg", "logo": { "@type": "ImageObject", "url": `${BASE}/logo_katzenberg_new.png` } },
  "image": `${BASE}/german_shepherd_anatomy.png`,
  "url": `${BASE}/anatomia-owczarka-niemieckiego`,
  "mainEntityOfPage": `${BASE}/anatomia-owczarka-niemieckiego`
};

/* ─── FAQ ITEM ─── */
const FAQItem = ({ q, a, isOpen, onToggle, idx }) => (
  <div className="border border-[#2A2A2D] hover:border-[#D4AF37]/20 transition-colors" data-testid={`anatomy-faq-${idx}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between p-5 md:p-6 text-left group">
      <span className="text-sm md:text-base text-[#F5F5F5] pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
      <ChevronDown className={`w-4 h-4 text-[#D4AF37] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="px-5 md:px-6 pb-5 md:pb-6 text-sm text-[#B8B8B8]/70 leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

/* ─── MAIN COMPONENT ─── */
const AnatomyPage = () => {
  const [activeLayer, setActiveLayer] = useState("external");
  const [selectedPart, setSelectedPart] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [infoTab, setInfoTab] = useState("opis");
  const [openFaq, setOpenFaq] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const interactiveRef = useRef(null);
  const imageContainerRef = useRef(null);

  const currentLayer = LAYERS.find(l => l.id === activeLayer);
  const selectedData = externalParts.find(p => p.id === selectedPart);

  // Calculate zoom transform for selected part
  const getZoomStyle = () => {
    if (!selectedPart) return { transform: "scale(1)", transformOrigin: "center center" };
    const part = externalParts.find(p => p.id === selectedPart);
    if (!part) return { transform: "scale(1)", transformOrigin: "center center" };
    return {
      transform: "scale(1.25)",
      transformOrigin: `${part.x}% ${part.y}%`,
    };
  };

  const handleLayerClick = (layer) => {
    setActiveLayer(layer.id);
    setSelectedPart(null);
  };

  const handlePartClick = (partId) => {
    setSelectedPart(partId === selectedPart ? null : partId);
    setInfoTab("opis");
  };

  const scrollToInteractive = () => {
    interactiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-[#0B0B0D] min-h-screen">
      <KennelSEO
        title="Anatomia Owczarka Niemieckiego – budowa, ruch i standard FCI | Wolfe vom Katzenberg"
        description="Interaktywny atlas anatomii owczarka niemieckiego. Poznaj budowę ciała, układ kostny, mięśnie, ruch i znaczenie dla zdrowia i hodowli. Przewodnik od hodowli Wolfe vom Katzenberg."
        canonical={`${BASE}/anatomia-owczarka-niemieckiego`}
        ogImage={`${BASE}/german_shepherd_anatomy.png`}
        schema={[articleSchema, localBusinessSchema, faqSchema(anatomyFaq), breadcrumbSchema([
          { name: "Start", href: "/" },
          { name: "Wiedza o rasie", href: "/wiedza-o-rasie" },
          { name: "Anatomia owczarka niemieckiego", href: "/anatomia-owczarka-niemieckiego" }
        ])]}
      />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-[#141416] border-b border-[#2A2A2D]">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 40px)" }} />
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.04] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="max-w-5xl mx-auto px-4 md:px-12 py-16 md:py-24 relative">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-[10px] tracking-wider text-[#B8B8B8]/40 mb-8" data-testid="anatomy-breadcrumbs">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">START</Link>
            <span>/</span>
            <Link to="/wiedza-o-rasie" className="hover:text-[#D4AF37] transition-colors">WIEDZA O RASIE</Link>
            <span>/</span>
            <span className="text-[#D4AF37]">ANATOMIA</span>
          </nav>

          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">
            INTERAKTYWNY PRZEWODNIK
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-serif text-[clamp(1.8rem,4.5vw,3.2rem)] text-[#F5F5F5] leading-tight mb-6" data-testid="anatomy-h1"
          >
            Anatomia Owczarka Niemieckiego
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="w-16 h-px bg-[#D4AF37]/40 mb-6" />
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-[#B8B8B8] text-sm md:text-base leading-relaxed max-w-3xl mb-10"
          >
            Poznaj szczegółową budowę owczarka niemieckiego — od zewnętrznej sylwetki po układ kostny, mięśniowy i biomechanikę ruchu. Dowiedz się, dlaczego każda część ciała ma znaczenie dla zdrowia, funkcjonalności i oceny wystawowej psa.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-3"
          >
            <button onClick={scrollToInteractive} className="px-7 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all duration-300 flex items-center gap-2" data-testid="hero-cta-explore">
              POZNAJ BUDOWĘ <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <Link to="/psy" className="px-7 py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2" data-testid="hero-cta-dogs">
              NASZE PSY <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── INTERACTIVE MODEL ── */}
      <section ref={interactiveRef} className="px-4 md:px-12 py-10 md:py-16">
        <div className="max-w-6xl mx-auto">
            <motion.div
              key="external-layer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start"
            >
              {/* Dog image with hotspots */}
              <div className="flex-1 relative w-full">
                <div ref={imageContainerRef} className="relative w-full select-none overflow-hidden">
                  {/* Zoomable image wrapper */}
                  <motion.div
                    animate={getZoomStyle()}
                    transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    <img
                      src="/german_shepherd_anatomy.png"
                      alt="Anatomia owczarka niemieckiego — budowa zewnętrzna"
                      className="w-full h-auto block"
                      data-testid="anatomy-main-image"
                      draggable={false}
                      onLoad={() => setImageLoaded(true)}
                    />
                  </motion.div>
                  {/* Bottom gradient to hide image artifacts */}
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0B0B0D] to-transparent pointer-events-none z-[5]" />

                  {/* Selected area glow — enhanced */}
                  <AnimatePresence>
                  {selectedPart && (() => {
                    const part = externalParts.find(p => p.id === selectedPart);
                    return part ? (
                      <motion.div
                        key={`glow-${part.id}`}
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ duration: 0.4 }}
                        className="absolute pointer-events-none"
                        style={{
                          left: `${part.x}%`, top: `${part.y}%`,
                          transform: "translate(-50%, -50%)",
                          width: "160px", height: "160px",
                          background: "radial-gradient(circle, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0.08) 35%, rgba(212,175,55,0.02) 55%, transparent 70%)",
                          borderRadius: "50%",
                          filter: "blur(4px)"
                        }}
                      />
                    ) : null;
                  })()}
                  </AnimatePresence>

                  {/* Connecting line from hotspot to panel edge */}
                  <AnimatePresence>
                  {selectedPart && (() => {
                    const part = externalParts.find(p => p.id === selectedPart);
                    return part ? (
                      <motion.svg
                        key={`line-${part.id}`}
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        exit={{ pathLength: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="absolute inset-0 w-full h-full pointer-events-none z-[6] hidden lg:block"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <motion.line
                          x1={part.x} y1={part.y}
                          x2="100" y2={Math.min(Math.max(part.y, 15), 55)}
                          stroke="#D4AF37"
                          strokeWidth="0.15"
                          strokeDasharray="1 1"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.4 }}
                          exit={{ pathLength: 0, opacity: 0 }}
                          transition={{ duration: 0.6, delay: 0.1 }}
                        />
                      </motion.svg>
                    ) : null;
                  })()}
                  </AnimatePresence>

                  {/* Hotspots — staggered entrance */}
                  {externalParts.map((part, idx) => (
                    <motion.div
                      key={part.id}
                      initial={imageLoaded ? { opacity: 0, scale: 0 } : false}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: imageLoaded ? idx * 0.06 : 0, duration: 0.3, type: "spring", stiffness: 400, damping: 20 }}
                      className="absolute cursor-pointer group"
                      style={{ left: `${part.x}%`, top: `${part.y}%`, transform: "translate(-50%, -50%)", zIndex: selectedPart === part.id ? 20 : 10 }}
                      onClick={() => handlePartClick(part.id)}
                      onMouseEnter={() => setHoveredPart(part.id)}
                      onMouseLeave={() => setHoveredPart(null)}
                      data-testid={`hotspot-${part.id}`}
                    >
                      {/* Pulse ring */}
                      <div
                        className="absolute rounded-full border-2 animate-ping"
                        style={{
                          borderColor: selectedPart === part.id ? "#E63946" : "rgba(230,57,70,0.5)",
                          width: selectedPart === part.id ? "40px" : "28px",
                          height: selectedPart === part.id ? "40px" : "28px",
                          top: "50%", left: "50%",
                          transform: "translate(-50%, -50%)",
                          animationDuration: selectedPart === part.id ? "1.5s" : "2.5s",
                          opacity: selectedPart === part.id ? 0.5 : 0.3
                        }}
                      />
                      {/* Second pulse ring for selected */}
                      {selectedPart === part.id && (
                        <div
                          className="absolute rounded-full border animate-ping"
                          style={{
                            borderColor: "rgba(230,57,70,0.2)",
                            width: "52px", height: "52px",
                            top: "50%", left: "50%",
                            transform: "translate(-50%, -50%)",
                            animationDuration: "2s",
                            animationDelay: "0.3s",
                            opacity: 0.15
                          }}
                        />
                      )}
                      {/* Number badge */}
                      <motion.div
                        animate={{
                          width: selectedPart === part.id ? 36 : hoveredPart === part.id ? 28 : 20,
                          height: selectedPart === part.id ? 36 : hoveredPart === part.id ? 28 : 20,
                          backgroundColor: selectedPart === part.id ? "#E63946" : hoveredPart === part.id ? "rgba(230,57,70,0.9)" : "rgba(230,57,70,0.7)",
                          boxShadow: selectedPart === part.id
                            ? "0 0 16px rgba(230,57,70,0.6), 0 0 32px rgba(212,175,55,0.3)"
                            : hoveredPart === part.id
                              ? "0 0 12px rgba(230,57,70,0.4), 0 0 20px rgba(212,175,55,0.2)"
                              : "0 0 6px rgba(230,57,70,0.3), 0 0 12px rgba(212,175,55,0.15)",
                        }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="relative rounded-full flex items-center justify-center border border-[#D4AF37]/40"
                      >
                        <span className={`font-bold transition-all duration-200 ${
                          selectedPart === part.id ? "text-[10px] text-white"
                          : hoveredPart === part.id ? "text-[9px] text-white"
                          : "text-[7px] text-white/90"
                        }`}>
                          {idx + 1}
                        </span>
                      </motion.div>
                      {/* Tooltip */}
                      <AnimatePresence>
                      {(hoveredPart === part.id || selectedPart === part.id) && (
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 4, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-1/2 -translate-x-1/2 -top-9 whitespace-nowrap bg-[#D4AF37] text-[#0B0B0D] text-[9px] tracking-[0.1em] font-bold px-2.5 py-1 pointer-events-none z-30"
                        >
                          {part.name}
                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-[#D4AF37]" />
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>

                {/* Body parts legend — staggered */}
                <div className="flex flex-wrap gap-1.5 mt-6 justify-center" data-testid="anatomy-legend">
                  {externalParts.map((part, idx) => (
                    <motion.button
                      key={part.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + idx * 0.04 }}
                      onClick={() => handlePartClick(part.id)}
                      className={`text-[9px] tracking-wider px-2.5 py-1.5 border transition-all duration-200 flex items-center gap-1.5 ${
                        selectedPart === part.id
                          ? "bg-[#D4AF37] text-[#0B0B0D] border-[#D4AF37] font-bold shadow-[0_0_12px_rgba(212,175,55,0.3)]"
                          : "border-[#2A2A2D] text-[#B8B8B8]/50 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                      }`}
                      data-testid={`legend-${part.id}`}
                    >
                      <span className="w-4 h-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[8px] font-bold text-[#D4AF37]">{idx + 1}</span>
                      {part.name.toUpperCase()}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Info panel */}
              <div className="lg:w-[420px] w-full lg:sticky lg:top-[140px]">
                <AnimatePresence mode="wait">
                  {selectedData ? (
                    <motion.div
                      key={selectedData.id}
                      initial={{ opacity: 0, x: 30, scale: 0.97 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -20, scale: 0.97 }}
                      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="bg-[#141416] border border-[#2A2A2D] overflow-hidden"
                      data-testid="anatomy-info-panel"
                    >
                      {/* Gold accent line at top */}
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent origin-left"
                      />
                      {/* Header */}
                      <div className="flex items-center justify-between p-5 border-b border-[#2A2A2D]">
                        <div>
                          <span className="text-[9px] tracking-[0.2em] text-[#D4AF37] block mb-1">ANATOMIA</span>
                          <h3 className="font-serif text-lg text-[#F5F5F5]">{selectedData.name}</h3>
                        </div>
                        <button onClick={() => setSelectedPart(null)} className="text-[#B8B8B8]/40 hover:text-[#F5F5F5] transition-colors w-8 h-8 flex items-center justify-center" data-testid="info-close-btn">
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Info tabs */}
                      <div className="flex border-b border-[#2A2A2D]" data-testid="info-tabs">
                        {INFO_TABS.map(tab => {
                          const TabIcon = tab.icon;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setInfoTab(tab.id)}
                              className={`flex-1 py-3 text-[9px] tracking-[0.1em] transition-all duration-200 flex flex-col items-center gap-1 ${
                                infoTab === tab.id
                                  ? "text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/[0.05]"
                                  : "text-[#B8B8B8]/40 hover:text-[#B8B8B8]/70"
                              }`}
                              data-testid={`tab-${tab.id}`}
                            >
                              <TabIcon className="w-3.5 h-3.5" />
                              {tab.label.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={infoTab}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15 }}
                          >
                            <span className="text-[8px] tracking-[0.15em] text-[#B8B8B8]/30 block mb-3">
                              {INFO_TABS.find(t => t.id === infoTab)?.level?.toUpperCase()}
                            </span>
                            <p className="text-[#B8B8B8] text-sm leading-relaxed">
                              {selectedData[infoTab]}
                            </p>
                          </motion.div>
                        </AnimatePresence>
                      </div>

                      {/* Audio placeholder */}
                      <div className="px-5 pb-5">
                        <button className="flex items-center gap-2 text-[#D4AF37]/30 text-[10px] tracking-wider" data-testid="audio-btn">
                          <Volume2 className="w-3.5 h-3.5" /> ODSŁUCHAJ OPIS (WKRÓTCE)
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="bg-[#141416] border border-[#2A2A2D] p-8 text-center"
                    >
                      <div className="w-14 h-14 mx-auto mb-5 border border-[#D4AF37]/15 flex items-center justify-center">
                        <Info className="w-6 h-6 text-[#D4AF37]/30" />
                      </div>
                      <p className="text-[#B8B8B8]/40 text-sm leading-relaxed mb-2">Kliknij na złoty punkt na obrazku</p>
                      <p className="text-[#B8B8B8]/25 text-xs">aby poznać szczegóły danej części ciała owczarka niemieckiego</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
        </div>
      </section>

      {/* ── SEO CONTENT: Budowa zewnętrzna ── */}
      <section className="bg-[#141416] border-y border-[#2A2A2D] px-4 md:px-12 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">BUDOWA</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6" data-testid="seo-h2-budowa">
              Budowa zewnętrzna owczarka niemieckiego
            </h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Owczarek niemiecki to pies średniej wielkości o lekko wydłużonej, mocnej i dobrze umięśnionej sylwetce. Jego budowa powinna łączyć siłę z elegancją, a proporcje ciała umożliwiać wydajny, oszczędzający energię kłus dalekiego zasięgu — cechę, dla której rasa została pierwotnie wyselekcjonowana.</p>
              <p>Prawidłowa budowa zewnętrzna owczarka niemieckiego obejmuje: klinowatą głowę z stojącymi uszami, silną szyję bez podgardla, wysoki i dobrze zaznaczony kłąb, prosty grzbiet lekko opadający ku zadowi, głęboką klatkę piersiową, dobrze kątowane kończyny przednie i tylne oraz ogon sięgający co najmniej do stawu skokowego.</p>
              <p>Stosunek długości tułowia do wysokości w kłębie powinien wynosić od 110% do 117%, co nadaje rasie charakterystyczny, lekko prostokątny zarys. Wysokość w kłębie wynosi 60–65 cm u psów i 55–60 cm u suk.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SEO CONTENT: Dlaczego budowa ma znaczenie ── */}
      <section className="px-4 md:px-12 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">DLACZEGO TO WAŻNE</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6" data-testid="seo-h2-znaczenie">
              Dlaczego budowa psa ma znaczenie
            </h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5 mt-4">
            {[
              { title: "Wpływ na zdrowie", text: "Prawidłowa budowa zmniejsza ryzyko dysplazji stawów, problemów kręgosłupa i przedwczesnego zużycia chrząstek. Pies o dobrych proporcjach żyje dłużej i zdrowiej." },
              { title: "Wpływ na ruch", text: "Owczarek niemiecki to kłusak dalekiego zasięgu. Prawidłowe kątowanie kończyn, mocne lędźwie i odpowiedni kąt zadu pozwalają na wydajny ruch godzinami bez zmęczenia." },
              { title: "Wpływ na pracę psa", text: "Pies robocz potrzebuje funkcjonalnej budowy. Silna klatka piersiowa zapewnia wytrzymałość, mocne łapy umożliwiają pracę w terenie, a prawidłowa psychika idzie w parze z dobrą budową." },
              { title: "Wpływ na wybór szczenięcia", text: "Znając prawidłową budowę, łatwiej ocenisz rodziców miotu i przewidzisz, jak będzie wyglądał dorosły pies. To klucz do świadomego wyboru szczenięcia z odpowiedzialnej hodowli." },
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-[#141416] border border-[#2A2A2D] p-6 group hover:border-[#D4AF37]/20 transition-all duration-500"
              >
                <h3 className="text-[#F5F5F5] text-sm font-medium mb-3 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                <p className="text-[#B8B8B8]/50 text-xs leading-relaxed group-hover:text-[#B8B8B8]/70 transition-colors">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO CONTENT: Proporcje i harmonia ── */}
      <section className="bg-[#141416] border-y border-[#2A2A2D] px-4 md:px-12 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">PROPORCJE</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">
              Kluczowe proporcje owczarka niemieckiego
            </h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[
              { label: "Tułów : Wysokość", value: "110–117%", desc: "Lekko prostokątna sylwetka" },
              { label: "Klatka piersiowa", value: "45–48%", desc: "Głębokość do wysokości w kłębie" },
              { label: "Czaszka : Kufa", value: "50 : 50", desc: "Równe proporcje głowy" },
              { label: "Kąt łopatki", value: "~45°", desc: "Do poziomu, z ramieniem ~90°" },
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="border border-[#2A2A2D] p-5 text-center group hover:border-[#D4AF37]/20 transition-colors"
              >
                <span className="text-[#D4AF37] font-serif text-2xl md:text-3xl block mb-2">{item.value}</span>
                <span className="text-[#F5F5F5] text-xs font-medium block mb-1">{item.label}</span>
                <span className="text-[#B8B8B8]/40 text-[10px] leading-relaxed">{item.desc}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-[#141416] border-y border-[#2A2A2D] px-4 md:px-12 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">FAQ</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-4" data-testid="seo-h2-faq">
              Najczęściej zadawane pytania o budowę owczarka
            </h2>
            <div className="w-12 h-px bg-[#D4AF37]/30" />
          </motion.div>
          <div className="space-y-3" data-testid="anatomy-faq-accordion">
            {anatomyFaq.map((item, idx) => (
              <FAQItem key={idx} idx={idx} q={item.q} a={item.a} isOpen={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
            ))}
          </div>
        </div>
      </section>

      {/* ── INTERNAL LINKS / CTA ── */}
      <section className="px-4 md:px-12 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2rem)] text-[#F5F5F5] mb-6">
              Poznaj nasze psy hodowlane
            </h2>
            <p className="text-[#B8B8B8]/60 text-sm leading-relaxed mb-10 max-w-2xl mx-auto">
              Nasze psy to przykład prawidłowej budowy owczarka niemieckiego – zgodnej ze standardem rasy, potwierdzone wynikami wystawowymi. Zobacz ich profile, rodowody i osiągnięcia.
            </p>
            <Link to="/psy" className="inline-flex items-center gap-2 px-10 py-3.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all duration-300" data-testid="cta-dogs">
              ZOBACZ NASZE PSY <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── COPYRIGHT ── */}
      <div className="border-t border-[#2A2A2D] px-4 md:px-12 py-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.1em] text-[#B8B8B8]/30">
            Projekt graficzny i koncepcja &copy; <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors">apisdesign.pl</a> &mdash; Wszelkie prawa zastrzeżone
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnatomyPage;
